/* eslint-disable */
import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { getData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";
import { getCustomStyles } from "Config/Config";

const CalendarFollowersOffcanvas = ({ afterSave = () => {} }) => {
  const { t } = useTranslation();
  const params = useParams();
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));

  const userObj = {
    label: `${userInfo.name ?? userInfo.email} ${userInfo.surname ?? ""}`.trim(),
    value: userInfo._id,
  };

  const [searchInput, setSearchInput] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [selectedValue, setSelectedValue] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const [followers, setFollowers] = useState([]);
  const [isLoadingFollowers, setIsLoadingFollowers] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(null);

  const [errors, setErrors] = useState({});

  // fetch user search options
  const getUserOptions = async () => {
    try {
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_USERLIST +
        `?token=${token}&userstring=${searchInput}`;

      const response = await getData(requestUrl);

      if (response.status) {
        const apiData = response.data.map((item) => ({
          value: item.value,
          label: item.label,
        }));
        const userExists = apiData.some((item) => item.value === userObj.value);
        setUserOptions(userExists ? apiData : [...apiData, userObj]);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  // fetch existing calendar followers
  const getCalendarFollowers = async () => {
    setIsLoadingFollowers(true);
    try {
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_CALENDAR_FOLLOWERS +
        `/${params.id}?token=${token}`;

      const response = await getData(requestUrl);

      if (response.status) {
        setFollowers(response.data);
      }
    } catch (error) {
      console.log(error.message);
    }
    setIsLoadingFollowers(false);
  };

  // add followers
  const addFollowersHandler = async () => {
    if (selectedIds.length === 0) {
      setErrors({ followerWarning: t("Please select at least one user") });
      return;
    }

    setIsSaving(true);
    try {
      let requestUrl =
        url.API_BASE_URL +
        url.API_MANAGE_CALENDAR_FOLLOWERS +
        `/${params.id}?token=${token}&action=add`;

      const response = await putData(requestUrl, { selectedfollowerids: selectedIds });

      if (response.status) {
        setSelectedValue(null);
        setSelectedIds([]);
        setUserOptions([]);
        setSearchInput("");
        await getCalendarFollowers();
        afterSave();
      }
    } catch (error) {
      console.log(error.message);
    }
    setIsSaving(false);
  };

  // remove a follower
  const removeFollowerHandler = async (followerId) => {
    setIsRemoving(followerId);
    try {
      let requestUrl =
        url.API_BASE_URL +
        url.API_MANAGE_CALENDAR_FOLLOWERS +
        `/${params.id}?token=${token}&action=remove`;

      const response = await putData(requestUrl, { selectedfollowerids: [followerId] });

      if (response.status) {
        await getCalendarFollowers();
        afterSave();
      }
    } catch (error) {
      console.log(error.message);
    }
    setIsRemoving(null);
  };

  useEffect(() => {
    if (searchInput.length > 0) {
      getUserOptions();
    }
  }, [searchInput]);

  useEffect(() => {
    if (params.id) {
      getCalendarFollowers();
    }
  }, [params.id]);

  return (
    <div
      className="offcanvas offcanvas-end"
      tabIndex="-1"
      id="calendarFollowersOffcanvas"
      aria-labelledby="calendarFollowersOffcanvasLabel"
      style={{ width: "80%" }}
    >
      <div className="offcanvas-header bg-primary text-white px-4 py-3">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-2 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary icon-md">
              group
            </span>
          </div>
          <div>
            <h5 className="fw-bold text-white mb-0" id="calendarFollowersOffcanvasLabel">
              {t("Calendar Followers")}
            </h5>
            <p className="text-white fs-sm mb-0">
              {t("Manage who can view and manage this calendar")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
        ></button>
      </div>

      <div className="offcanvas-body p-4 d-flex flex-column gap-4">
        {/* Info note */}
        <div className="d-flex align-items-start gap-2 p-3 bg-light border border-gray-300 rounded-10">
          <span className="material-symbols-outlined text-primary fs-lg mt-1">info</span>
          <p className="mb-0 fs-sm text-gray">
            {t("Add managers who will be able to add or modify records in your calendar.")}
          </p>
        </div>

        {/* Add followers section */}
        <div className="border border-gray-300 rounded-10 p-3 shadow-sm bg-light">
          <h6 className="fw-semibold mb-3">{t("Add Managers")}</h6>
          <div className="d-flex align-items-start gap-2">
            <div className="flex-fill">
              <Select
                isMulti
                placeholder={t("Search users by name or email")}
                options={userOptions}
                value={selectedValue}
                onChange={(val) => {
                  setSelectedValue(val);
                  setSelectedIds(val ? val.map((item) => item.value) : []);
                  if (errors.followerWarning) {
                    setErrors({});
                  }
                }}
                onInputChange={(val) => setSearchInput(val)}
                styles={getCustomStyles(errors.followerWarning)}
              />
              {errors.followerWarning && (
                <div className="invalid-feedback d-block">
                  * {errors.followerWarning}
                </div>
              )}
            </div>
            <button
              type="button"
              className="btn btn-primary d-flex align-items-center gap-2 flex-shrink-0"
              onClick={addFollowersHandler}
              disabled={isSaving}
            >
              <span className="material-symbols-outlined fs-lg">person_add</span>
              {t("Add")}
              {isSaving && (
                <div className="spinner-border spinner-border-sm ms-1" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Existing followers list */}
        <div>
          <h6 className="fw-semibold mb-3">
            {t("Current Followers")}
            {followers.length > 0 && (
              <span className="badge bg-primary ms-2">{followers.length}</span>
            )}
          </h6>

          {isLoadingFollowers ? (
            <div className="d-flex flex-column gap-2">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="d-flex align-items-center gap-3 p-3 border border-gray-200 rounded-10 bg-light placeholder-glow"
                >
                  <div
                    className="rounded-circle placeholder bg-gray-300"
                    style={{ width: 36, height: 36, flexShrink: 0 }}
                  ></div>
                  <div className="flex-fill">
                    <div className="placeholder col-6 bg-gray-300 rounded mb-1"></div>
                    <div className="placeholder col-4 bg-gray-300 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : followers.length === 0 ? (
            <p className="text-muted fs-sm">
              {t("No calendar followers added yet.")}
            </p>
          ) : (
            <ul className="list-unstyled d-flex flex-column gap-2">
              {followers.map((follower) => (
                <li
                  key={follower._id}
                  className="d-flex align-items-center gap-3 p-3 border border-gray-200 rounded-10 bg-white"
                >
                  <div
                    className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white fw-semibold flex-shrink-0"
                    style={{ width: 36, height: 36, fontSize: 14 }}
                  >
                    {follower.userimage ? (
                      <img
                        src={follower.userimage}
                        alt=""
                        className="rounded-circle w-100 h-100 object-fit-cover"
                      />
                    ) : (
                      (follower.name || follower.email || "?")[0].toUpperCase()
                    )}
                  </div>
                  <div className="flex-fill">
                    <p className="fw-semibold mb-0 fs-sm">
                      {`${follower.name || ""} ${follower.surname || ""}`.trim() || follower.email}
                    </p>
                    <p className="text-muted mb-0 fs-xs">{follower.email}</p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger d-flex align-items-center"
                    onClick={() => removeFollowerHandler(follower._id)}
                    disabled={isRemoving === follower._id}
                    title={t("Remove")}
                  >
                    {isRemoving === follower._id ? (
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : (
                      <span className="material-symbols-outlined fs-lg">
                        person_remove
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarFollowersOffcanvas;
